import { fbt, useLocaleContext } from 'fbtee'
import { Martini, X } from 'lucide-react'
import { useLocation } from 'react-router-dom'
import { BottomBanner } from './BottomBanner'
import { UserAvatar } from './UserAvatar'
import { ThumbStack } from './profile/ThumbStack'
import { usePregameRsvpPrompt } from '../hooks/usePregameRsvpPrompt'
import { setPregameRsvp } from '../hooks/usePregames'
import { friendFallbackName } from '../lib/commonStrings'
import { pregameWhenLabel } from '../lib/pregameLabels'

const chip = 'flex-none cursor-pointer rounded-full px-3 py-1 text-xs font-semibold whitespace-nowrap transition-colors'

// From the day before an event Home shows as a hero, asks above the nav whether the viewer is going to the
// pre-game they were invited to — the festival check-in prompt's shape, so an
// unanswered invite is hard to miss. Home only: the event page's pre-game card
// already asks.
export function PregameRsvpBanner() {
  const { pathname } = useLocation()
  const { locale } = useLocaleContext()
  const { prompt, uid, dismiss } = usePregameRsvpPrompt()
  if (!prompt || !uid || pathname !== '/') return null
  const { pregame, event, host, tomorrow } = prompt
  const hostName = host?.displayName ?? friendFallbackName()
  // "Tomorrow" replaces a festival day's weekday rather than stacking on it.
  const when = pregameWhenLabel(tomorrow ? { time: pregame.time } : pregame, locale)
  const details = [
    tomorrow ? [fbt('Tomorrow', 'Pre-game banner: the pre-game is tomorrow'), when].filter(Boolean).join(' ') : when,
    pregame.placeName || pregame.address,
  ].filter(Boolean).join(' · ')
  const answer = (status: 'going' | 'not_going') => setPregameRsvp(pregame, uid, status).catch(() => {}) // toasted

  return (
    <BottomBanner testId="pregame-rsvp-banner">
      {/* The event with its host in the corner, as Crew's activity feed pairs a plan with a person. */}
      <span className="relative flex-none self-start">
        <ThumbStack srcs={[event.imageUrl]} icon={Martini} size="md" />
        <UserAvatar profile={host} fallbackName={hostName} size="xs" className="absolute -bottom-1 -right-1 z-10 ring-2 ring-background" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="line-clamp-2 text-[13px] font-semibold leading-tight">
          {fbt(
            'Going to ' + fbt.param('host', hostName) +
              "'s pre-game for " + fbt.param('event', event.name) + '?',
            'Banner asking on the day of an event whether the user is going to the pre-game they were invited to',
          )}
        </div>
        <div className="mt-1 flex items-center gap-1">
          <div className="min-w-0 flex-1 truncate text-[11px] leading-tight text-muted-foreground">{details}</div>
          <button
            type="button"
            className={`${chip} bg-primary text-primary-foreground hover:bg-primary/90`}
            onClick={() => answer('going')}
          >
            {fbt("I'm going", 'Pre-game banner button saying the user is going')}
          </button>
          <button
            type="button"
            className="flex-none cursor-pointer px-1.5 py-1 text-xs font-medium whitespace-nowrap text-muted-foreground hover:text-foreground"
            onClick={() => answer('not_going')}
          >
            {fbt("Can't go", 'Pre-game banner button saying the user is not going')}
          </button>
        </div>
      </div>
      <button
        type="button"
        onClick={() => dismiss(pregame.id)}
        className="-mr-1.5 flex-none self-start p-1 text-muted-foreground hover:text-foreground cursor-pointer"
        aria-label={fbt('Dismiss', 'Accessible label for the close button on the pre-game banner')}
      >
        <X className="size-3.5" />
      </button>
    </BottomBanner>
  )
}
